import {
  Box,
  Flex,
  IconButton,
  Link as ChakraLink,
  ListItem,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import useUser from "../../hooks/useUser";
import { SelectItem } from "../../lib/types/SelectItem";
import { Preferences } from "../../lib/types/UserPreference";
import { PencilAltSolid } from "../icons/PencilAlt";
import { PinnedItemsModal } from "./PinnedItemsModal";

export interface PinnedItemsProps {
  items: SelectItem[];
  selectedItemValues: Array<string>;
  label: string;
  savePreferencesKey: keyof Preferences;
  pathValue: string;
}

export function PinnedItems({
  items,
  selectedItemValues,
  label,
  savePreferencesKey,
  pathValue,
}: PinnedItemsProps) {
  const user = useUser();
  const location = useLocation();
  const { userPreferences, savePreferencesHelper } = user;
  const [pinnedItemIds, setPinnedItemIds] = React.useState<Array<string>>([]);
  const [pinnedItems, setPinnedItems] = React.useState<Array<SelectItem>>([]);

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    const pinnedItems = items.filter((item: any) => {
      return pinnedItemIds.includes(item.value);
    });
    setPinnedItems(pinnedItems);
  }, [items, pinnedItemIds, setPinnedItems]);

  const handleModalVisibility = (open: boolean) => {
    setIsModalOpen(open);
  };

  const onSaveClick = async (selectedItems: string[]) => {
    setIsLoading(true);
    let newPreferences: Preferences = {};
    newPreferences[savePreferencesKey] = selectedItems;
    try {
      await savePreferencesHelper(newPreferences);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      handleModalVisibility(false);
    }
  };

  React.useEffect(() => {
    setPinnedItemIds(selectedItemValues ?? []);
  }, [selectedItemValues]);

  return (
    <>
      {items.length > 0 && (
        <Box marginLeft={8} marginRight={7} marginBottom={6}>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            position={"sticky"}
            top={0}
            left={0}
          >
            <Text
              textTransform="uppercase"
              fontSize="xs"
              letterSpacing="wider"
              fontWeight="medium"
              color="brand.50"
            >
              {`Pinned ${label}`}
            </Text>

            <IconButton
              onClick={() => handleModalVisibility(true)}
              variant="unstyled"
              minW="auto"
              height="auto"
              padding={0}
              title={`Edit Pinned ${label}`}
              aria-label={`Edit Pinned ${label}`}
              icon={<PencilAltSolid color="brand.50" height={5} width={5} />}
            ></IconButton>
          </Flex>

          {isLoading || !userPreferences ? (
            <Text paddingY={2} color="brand.100">
              {`Loading ${label}...`}
            </Text>
          ) : (
            <>
              {pinnedItems && pinnedItems.length > 0 ? (
                <UnorderedList listStyleType="none">
                  {pinnedItems.map((item) => (
                    <ListItem paddingY={2} key={item.value} fontWeight="medium">
                      <ChakraLink
                        as={Link}
                        to={`/quotes/${pathValue}/${item.value}`}
                        color={
                          location.pathname.includes(item.value)
                            ? "#C8E6FE"
                            : "brand.100"
                        }
                        transition="color 0.2s"
                      >
                        {`${item.label}`}
                      </ChakraLink>
                    </ListItem>
                  ))}
                </UnorderedList>
              ) : (
                <Text paddingY={2} color="brand.100">
                  {`No ${label} Pinned`}
                </Text>
              )}
            </>
          )}
        </Box>
      )}
      <PinnedItemsModal
        items={items ?? []}
        selectedItemValues={selectedItemValues}
        open={isModalOpen}
        handleOpen={handleModalVisibility}
        handleSave={onSaveClick}
        isLoading={isLoading}
        label={label}
      />
    </>
  );
}
